import { useEffect } from 'react';

import { ESCAPE } from 'approot/shared/ally/keyboard.hooks';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';
import { Box } from 'approot/shared/design-system/box/box.style';
import { TTSContainer } from './tts.style';
import { useTeacherOrStudentClassSettings } from '../present/activity-present-settings.hooks';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import { useTTSAudio } from './use-tts-audio';
import TTSListenButton from './controls/tts-listen-button';
import TTSStartAgainButton from './controls/tts-start-again-button';
import TTSPlayPauseButton from './controls/tts-play-pause-button';
import TTSCloseButton from './controls/tts-close-button';
import { ttsPushDataTrackingEvent } from './logic/tts-push-data-tracking-event';

type Props = {
  componentId: string;
  componentType: string;
};

const TTSButton = ({ componentId, componentType }: Props) => {
  const { version, lang, contentType, contentId, isPublic } =
    useGetInteractiveLessonParams();

  const {
    isAudioPlaying,
    isInPlayMode,
    isAudioPaused,
    loadingAudio,
    containerRef,
    playAudio,
    pauseAudio,
    restartAudio,
    stopAudio,
    setIsInPlayMode,
    handlePlayAudio,
    tearDown,
  } = useTTSAudio({ componentId, version, lang, contentType });

  const { ttsEnabled } = useTeacherOrStudentClassSettings();

  useEffect(() => {
    return () => tearDown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // listen for escape key
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === ESCAPE && isInPlayMode) {
        event.preventDefault();
        event.stopPropagation();
        setIsInPlayMode(false);
      }
    };

    document.addEventListener('keydown', handleEscapeKey, true);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey, true);
    };
  }, [isInPlayMode, setIsInPlayMode]);

  if ((!isPublic && !ttsEnabled) || !version) {
    return null;
  }
  return (
    <Box
      role="group"
      aria-label="Text to speech controls"
      $marginBottom={16}
      tabIndex={-1}
      ref={containerRef}
      data-selector="btn-tts-controls"
    >
      <TTSContainer $isInPlayMode={isInPlayMode}>
        {!isInPlayMode || loadingAudio ? (
          <TooltipWrapper hoverText="Listen to text">
            <TTSListenButton
              onClick={() => {
                handlePlayAudio();
                ttsPushDataTrackingEvent({
                  componentId,
                  componentType,
                  contentId,
                  contentType,
                  lang,
                });
              }}
              loadingAudio={loadingAudio}
            />
          </TooltipWrapper>
        ) : (
          <>
            <TTSPlayPauseButton
              isAudioPlaying={isAudioPlaying}
              isAudioPaused={isAudioPaused}
              onClick={() => {
                if (isAudioPlaying) {
                  pauseAudio();
                } else {
                  playAudio(componentId);
                  ttsPushDataTrackingEvent({
                    componentId,
                    componentType,
                    contentId,
                    contentType,
                    lang,
                  });
                }
              }}
            />
            <TTSStartAgainButton
              onClick={() => {
                restartAudio(componentId);
                ttsPushDataTrackingEvent({
                  componentId,
                  componentType,
                  contentId,
                  contentType,
                  lang,
                });
              }}
            />
            <TTSCloseButton
              onClick={() => {
                stopAudio(componentId);
                setIsInPlayMode(false);
                containerRef.current?.focus();
              }}
            />
          </>
        )}
      </TTSContainer>
    </Box>
  );
};

export default TTSButton;
